import axios from 'axios';
import { saveAs } from 'file-saver';
import Cookies from 'js-cookie';

/**
 * Fetches projects from the server, optionally filtered by status.
 * 
 * @async
 * @function fetchProjects
 * @param {string} [status] - The status of projects to filter by ('accepted', 'pending', or 'rejected'). 
 *                            If not provided, all projects will be fetched.
 * @returns {Promise<Object[]>} A promise that resolves to an array of project objects.
 * 
 * @throws Will throw an error if the network request fails or the server returns an error.
 * 
 */
export async function fetchProjects(status) {
    try {
        let response;

        if (status !== undefined) {
            response = await fetch(`/api/projects/status/${status}`);
        }
        else {
            response = await fetch(`/api/projects`);
        }
        return await response.json();
    } catch (error) {
        console.error('Error fetching projects:', error);
        throw error;
    }
};


/**
 * Fetches projects from the server, optionally filtered by status.
 * 
 * @async
 * @function fetchPublishedProjects - The status of projects to filter by ('accepted', 'pending', or 'rejected'). 
 *                            If not provided, all projects will be fetched.
 * @returns {Promise<Object[]>} A promise that resolves to an array of project objects.
 * 
 * @throws Will throw an error if the network request fails or the server returns an error.
 * 
 */
export async function fetchPublishedProjects() {
    try {
        const response = await fetch(`/api/projects/published`);

        return await response.json();
    } catch (error) {
        console.error('Error fetching published projects:', error);
        throw error;
    }
};

export async function updateStatus(id, status) {
    try {

        const token = Cookies.get("authToken");

        const response = await fetch(`/api/projects/${id}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "authToken": token
            },
            body: JSON.stringify({ status }),
        })
        return await response;
    } catch (error) {
        console.error('Error fetching projects:', error);
        throw error;
    }
};

export async function downloadCSV() {
    try {
        const response = await fetch(`/api/users/download`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            },
        })
        if (response.ok) {
            const csvData = await response.text();
            const url = window.URL.createObjectURL(new Blob([csvData]));
            console.log(response);
            var blob = new Blob([csvData], {
                type: "text/plain;charset=utf-8",
            });
            saveAs(blob, `Students.csv`);
        }

        return await response;
    } catch (error) {
        console.error('Error fetching projects:', error);
        throw error;
    }
};

export async function downloadCSVClients(semester_id) {
    try {
        const response = await fetch(`/api/users/download/clients/${semester_id}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            },
        })
        if (response.ok) {
            const csvData = await response.text();
            const url = window.URL.createObjectURL(new Blob([csvData]));
            console.log(response);
            var blob = new Blob([csvData], {
                type: "text/plain;charset=utf-8",
            });
            saveAs(blob, `Client Data.csv`);
        }

        return await response;
    } catch (error) {
        console.error('Error fetching projects:', error);
        throw error;
    }
};


export async function downloadCSVTeams(semesterID) {
    try {
        const response = await fetch(`/api/teams/download`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ semesterID }),
        })
        if (response.ok) {
            const csvData = await response.text();
            const url = window.URL.createObjectURL(new Blob([csvData]));
            var blob = new Blob([csvData], {
                type: "text/plain;charset=utf-8",
            });
            saveAs(blob, `Teams Data.csv`);
        }

        return await response;
    } catch (error) {
        console.error('Error fetching projects:', error);
        throw error;
    }
};


export async function processAllocation(hours) {
    try {
        const response = await fetch(`/api/project-allocation/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ hours }),
        })

        return await response;
    } catch (error) {
        console.error('Error processing project allocation:', error);
        throw error;
    }
};

// export async function fetchAllocationData() {
//     try {
//         const response = await fetch(`/api/project-allocation`)
//         console.log('response in Api.js',response);
//         return await response.text();
//     } catch (error) {
//         console.error('Error fetching allocation:', error);
//         throw error;
//     }

// }

export async function downloadAllocation() {
    try {
        const response = await fetch(`/api/project-allocation/download`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json"
            },
        })
        if (response.ok) {
            const csvData = await response.text();
            const url = window.URL.createObjectURL(new Blob([csvData]));
            var blob = new Blob([csvData], {
                type: "text/plain;charset=utf-8",
            });
            saveAs(blob, `Team Allocation Data.csv`);
        }

        return await response;
    } catch (error) {
        console.error('Error fetching projects:', error);
        throw error;
    }
};

/**
 * Fetches all teams for given semester
 * 
 * @async
 * @function fetchTeamsBySemester
 * @param {number} semesterID
 * @returns {Promise<Team[]>} 
 * 
 */
export async function fetchTeamsBySemester(semesterID) {
    try {
        const response = await fetch(`/api/teams/semester/${semesterID}`);
        console.log('Response:', response);
        return await response.json();

    } catch (error) {
        console.error('Error fetching teams:', error);
        throw error;
    }
};
/**
 * Fetches all users from the server 
 * 
 * @async
 * @function fetchUsersByRole
 * @param {'student | admin | client'} role
 * @returns {Promise<User[]>} 
 * 
 * @throws Will throw an error if the network request fails or the server returns an error.
 * 
 */
export async function fetchUsersByRole(role) {
    try {
        const response = await fetch(`/api/users/role/${role}`);
        console.log('Response:', response);
        return await response.json();

    } catch (error) {
        console.error('Error fetching users:', error);
        throw error;
    }
};

export async function updatePublish(status, approved_projects = []) {

    try {
        const token = Cookies.get("authToken");

        const response = await fetch(`/api/projects/publish/${status}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "authToken": token
            },
            body: JSON.stringify({ status, approved_projects }),
        })
        return await response;
    } catch (error) {
        console.error('Error fetching projects:', error);
        throw error;
    }
};



export async function createProject(title, description, owner_id, special_requirements, available_resources, preferred_skills, project_deliverable, created, available_from, expiry, status, max_teams, project_number, semester_id, other_client_details) {
    try {
        const response = await fetch(`/api/projects/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ title, description, owner_id, special_requirements, available_resources, preferred_skills, project_deliverable, created, expiry, available_from, status, max_teams, project_number, semester_id, other_client_details }),
        })

        return await response;
    } catch (error) {
        console.error('Error fetching projects:', error);
        throw error;
    }
};


export async function createSemester(start_date, end_date, start_bidding_date, end_bidding_date, is_semester_one, proposal_deadline) {
    try {
        const response = await fetch(`/api/semesters/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ start_date, end_date, start_bidding_date, end_bidding_date, is_semester_one, proposal_deadline }),
        })

        const createdSemester = await response.json();
        return createdSemester;

    } catch (error) {
        console.error('Error creating semester:', error);
        throw error;
    }
};


export async function editProject(id, title, description, special_requirements, available_resources, preferred_skills, project_deliverable, expiry, max_teams, other_client_details) {
    try {
        console.log('editing project')
        const response = await fetch(`/api/projects/update/${id}`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ title, description, special_requirements, available_resources, preferred_skills, project_deliverable, expiry, max_teams, other_client_details }),
        })
        console.log(response);
        return response;
    } catch (error) {
        console.log('Error editing projects:', error);
        throw error;
    }
};

export async function fetchSemesters() {
    try {
        let response;

        response = await fetch(`/api/semesters`);

        return await response.json();
    } catch (error) {
        console.error('Error fetching semesters:', error);
        throw error;
    }
};
/**
 * Fetches a semester from the server 
 * 
 * @async
 * @function fetchSemester
 * @param {number} [semesterID] 
 * @returns {Promise<Semester>} A promise that resolves to a Team object.
 * 
 */
export async function fetchSemester(semesterID) {
    try {
        console.log('fetching semester ' + semesterID);
        const response = await fetch(`/api/semesters/${semesterID}`);
        console.log('Fetched semester:', response);
        return await response.json();

    } catch (error) {
        console.error('Error fetching semester:', error);
        throw error;
    }
};
/**
 * Fetches all projects from the server by semester
 * 
 * @async
 * @function fetchProjectsBySemester
 * @param {number} [semesterId] 
 * @returns {Promise<Project[]>} A promise that resolves to an array of project objects.
 * 
 * @throws Will throw an error if the network request fails or the server returns an error.
 * 
 */
export async function fetchProjectsBySemester(semesterId) {
    try {
        const response = await fetch(`/api/projects/semester/${semesterId}`);
        console.log('project 1:', response[0])
        return await response.json();
    } catch (error) {
        console.error('Error fetching projects:', error);
        throw error;
    }
};
/**
 * Fetches all projects from the server by user ID
 * 
 * @async
 * @function fetchProjectsByUser
 * @param {number} [userId] 
 * @returns {Promise<Project[]>} A promise that resolves to an array of project objects.
 * 
 * @throws Will throw an error if the network request fails or the server returns an error.
 * 
 */
export async function fetchProjectsByUser(userId) {
    try {
        const response = await fetch(`/api/projects/user/${userId}`);
        return await response.json();
    } catch (error) {
        console.error('Error fetching user projects:', error);
        throw error;
    }
};
/**
 * Fetches a user from the server using user ID
 * 
 * @async
 * @function fetchUser
 * @param {number} [userId] 
 * @returns {Promise<User>} A promise that resolves to a User object.
 * 
 * @throws Will throw an error if the network request fails or the server returns an error.
 * 
 */
export async function fetchUser(userId) {
    try {
        console.log('fetching data for user:' + userId);
        const response = await fetch(`/api/users/id/${userId}`);
        console.log('Response:', response);
        return await response.json();

    } catch (error) {
        console.error('Error fetching user:', error);
        throw error;
    }
};
/**
 * Fetches a user's team from the server 
 * 
 * @async
 * @function fetchTeam
 * @param {number} [userId] 
 * @returns {Promise<Team>} A promise that resolves to a Team object.
 * 
 * @throws Will throw an error if the network request fails or the server returns an error.
 * 
 */
export async function fetchTeam(userId) {
    try {
        const response = await fetch(`/api/teams/user/id/${userId}`);
        console.log('Response:', response);
        return await response.json();

    } catch (error) {
        console.error('Error fetching team:', error);
        throw error;
    }
};
/**
 * Fetches a user's team from the server 
 * 
 * @async
 * @function fetchTeams
 * @returns {Promise<Team[]>} 
 * 
 * @throws Will throw an error if the network request fails or the server returns an error.
 * 
 */
export async function fetchTeams() {
    try {
        const response = await fetch(`/api/teams`);
        console.log('Response:', response);
        const data = await response.json();
        console.log('data', data);
        return data;

    } catch (error) {
        console.error('Error fetching teams:', error);
        throw error;
    }
};
/**
 * Updates given attribute for User with given id
 * 
 * @async
 * @function updateUserDetails
 * @param {number} [userId] 
 * @param {string} [attribute]
 * @param {string} [newValue]
 * @returns {Promise<User>} A promise that resolves to a User object.
 *  
 */
export async function updateUserDetails(userId, attribute, newValue) {
    try {

        let res = await fetch(`/api/users/edit/${userId}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                "attribute": attribute,
                "newValue": newValue
            })
        });
        const resJson = await res.json();

        // Stores the resulting Auth Token
        Cookies.set("authToken", resJson.token);


        return
    } catch (error) {
        console.error('Error updating user details:', error);
    }
};
/**
 * Deletes User's Account by deleting their projects and user record
 * 
 * @async
 * @function deleteUser
 * @param {number} [userID] 
 * @returns {Promise<response>} 
 *  
 */
export async function deleteUser(userID) {
    try {
        const response = await axios.delete(`/api/users/id/${userID}`);
        return response.status;
    } catch (error) {
        console.error('Error deleting user:', error);
        throw error;
    }
};
/**
 * Deletes a Semester
 * 
 * @async
 * @function deleteSemester
 * @param {number} [semesterID] 
 * @returns {Promise<response>} 
 *  
 */
export async function deleteSemester(semesterID) {
    try {
        const response = await axios.delete(`/api/semesters/${semesterID}`);
        return response.status;
    } catch (error) {
        console.error('Error deleting semester:', error);
        throw error;
    }
};
/**
 * Updates given attribute for User with given id
 * 
 * @async
 * @function updateSemesterDetails
 * @param {number} [semesterID] 
 * @param {string} [attribute]
 * @param {string} [newValue]
 * @returns {Promise<Semester>} 
 *  
 */
export async function updateSemesterDetails(semesterID, attribute, newValue) {
    try {
        const response = await axios.put(`/api/semesters/edit/${semesterID}`, {
            attribute, newValue
        });
        return response.data; // return the updated data
    } catch (error) {
        console.error('Error updating details:', error);
        return null;
    }
};


export async function updatePreferences(team_id, project_id, preference) {

    try {
        const response = await fetch(`/api/preferences`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({ team_id, project_id, preference }),
        })
        return await response;
    } catch (error) {
        console.error('Error updating project preferences:', error);
        throw error;
    }
};

export async function fetchPreferences() {
    console.log('Fetch student preferences');
    try {
        let response;
        response = await fetch(`/api/preferences/`);
        return await response.json();
    } catch (error) {
        console.error('Error fetching preferences:', error);
        throw error;
    }
};

export async function deletePreferences(id) {
    try {
        let response;
        response = await axios.delete(`/api/preferences/${id}`);
        return response.status;
    } catch (error) {
        console.error('Error deleting preference:', error);
        throw error;
    }
};

export async function deleteProject(id) {
    try {
        const response = await axios.delete(`/api/projects/${id}`);
        return response.status;
    } catch (error) {
        console.error('Error deleting project:', error);
        throw error;
    }
};

/**
 * Fetches a project from the server by ID
 * 
 * @async
 * @function fetchProjectById
 * @param {number} [projectId] 
 * @returns {Promise<Project[]>} A promise that resolves to an array of project objects.
 * 
 * @throws Will throw an error if the network request fails or the server returns an error.
 * 
 */
export async function fetchProjectById(projectId) {
    try {
        const response = await fetch(`/api/projects/${projectId}`);
        console.log('project fetched:', response[0])
        return await response.json();
    } catch (error) {
        console.error('Error fetching project:', error);
        throw error;
    }
};

export async function getStatusSemesters(status) {
    try {
        const response = await fetch(`/api/semesters/status/${status}`);

        return await response.json();
    } catch (error) {
        console.error('Error fetching semesters:', error);
        throw error;
    }
};
